// src/tabs/logements/AvisResponseCopilot.jsx — Copilote de réponse aux avis
//
// OPTION SIMPLE (validée Dimitri). Sous-composant monté DANS la carte d'un avis,
// à la fois dans AvisLogementView (fiche logement) et ModerationAvisView (vue
// globale Messages). Il aide à RÉDIGER une réponse publique à un avis voyageur.
//
// ⚠️ Garde déonto/DGCCRF STRICTE — ce composant :
//   - NE modère RIEN (pas d'approuver/rejeter/masquer/éditer l'avis).
//   - NE publie RIEN (l'app ne poste jamais sur Airbnb/Booking ; Dimitri copie
//     le brouillon à la main).
//   - NE sauve RIEN en base (brouillon 100 % ÉPHÉMÈRE, zéro persistance).
//
// Flux :
//   ✨ Générer  → POST /api/sites?action=avis_generate_response { avis_id }
//                 → affiche le `draft` dans un <textarea> ÉDITABLE.
//   🔄 Régénérer → re-call (nouveau brouillon, temperature 0.7 côté serveur).
//   📋 Copier    → navigator.clipboard.writeText(contenu édité du textarea).
//   ↗️ Répondre sur Airbnb → ouvre la page de réponse Airbnb (airbnb only).

import { useState, useCallback } from "react"
import { supabase } from "../../supabase"
import { DS } from "../../lib/ui"

const BTN_BASE = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #ddd',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
}

export default function AvisResponseCopilot({ avis }) {
  const avisId = avis?.id || null
  const source = avis?.source || null
  const externalId = avis?.external_id || null

  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState("")
  const [hasDraft, setHasDraft] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)

  const generate = useCallback(async () => {
    if (!avisId) return
    setLoading(true)
    setError(null)
    setCopied(false)
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) throw new Error('Pas de session admin active.')

      const res = await fetch('/api/sites?action=avis_generate_response', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ avis_id: avisId }),
      })
      const json = await res.json()
      if (!res.ok || !json.ok) throw new Error(json.error || 'generation_failed')

      setDraft(json.draft || "")
      setHasDraft(true)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [avisId])

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(draft || "")
      setCopied(true)
    } catch (e) {
      setError('Copie impossible : ' + (e?.message || 'presse-papier indisponible'))
    }
  }, [draft])

  // Lien de réponse Airbnb (airbnb only). Booking = futur → bouton masqué.
  const airbnbRespondUrl =
    source === 'airbnb' && externalId
      ? `https://www.airbnb.fr/users/reviews?respond_to=${encodeURIComponent(externalId)}`
      : null

  return (
    <div
      data-testid={`avis-copilot-${avisId}`}
      style={{ marginTop: 10, borderTop: '1px dashed #e0e0e0', paddingTop: 10 }}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {!hasDraft && (
          <button
            type="button"
            data-testid={`avis-copilot-generate-${avisId}`}
            onClick={generate}
            disabled={loading}
            style={{ ...BTN_BASE, background: '#eef4fb', color: DS.navy, cursor: loading ? 'wait' : 'pointer' }}
          >
            ✨ Générer une réponse
          </button>
        )}

        {hasDraft && (
          <>
            <button
              type="button"
              data-testid={`avis-copilot-regenerate-${avisId}`}
              onClick={generate}
              disabled={loading}
              style={{ ...BTN_BASE, cursor: loading ? 'wait' : 'pointer' }}
            >
              🔄 Régénérer
            </button>
            <button
              type="button"
              data-testid={`avis-copilot-copy-${avisId}`}
              onClick={copy}
              style={{ ...BTN_BASE, background: '#eef4fb', color: DS.navy }}
            >
              📋 Copier
            </button>
            {airbnbRespondUrl && (
              <a
                data-testid={`avis-copilot-airbnb-${avisId}`}
                href={airbnbRespondUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...BTN_BASE, textDecoration: 'none', display: 'inline-block', color: '#c13584' }}
              >
                ↗️ Répondre sur Airbnb
              </a>
            )}
            {copied && (
              <span data-testid={`avis-copilot-copied-${avisId}`} style={{ fontSize: 11, color: DS.green, fontWeight: 700 }}>
                ✅ Copié
              </span>
            )}
          </>
        )}

        {loading && (
          <span style={{ fontSize: 11, color: '#888' }}>Génération…</span>
        )}
      </div>

      {error && (
        <div
          data-testid={`avis-copilot-error-${avisId}`}
          style={{ marginTop: 8, padding: 8, background: '#f8d7da', color: '#721c24', borderRadius: 6, fontSize: 12 }}
        >
          ⚠️ {error}
        </div>
      )}

      {hasDraft && (
        <>
          <textarea
            data-testid={`avis-copilot-textarea-${avisId}`}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setCopied(false) }}
            rows={5}
            style={{
              width: '100%', marginTop: 8, padding: 10, borderRadius: 6,
              border: '1px solid #ccc', fontSize: 13, fontFamily: DS.fontBody,
              resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          {airbnbRespondUrl && (
            <div data-testid={`avis-copilot-airbnb-hint-${avisId}`} style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              ↗️ ouvre la réponse si la fenêtre Airbnb est encore ouverte
            </div>
          )}
          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
            Brouillon éphémère — rien n'est enregistré ni publié. Copiez-collez le texte sur la plateforme.
          </div>
        </>
      )}
    </div>
  )
}
